import { supabase } from "@/integrations/supabase/client";
import { trackGoogleAdsConversion } from "./analytics";

export type EventType =
  | "page_view"
  | "button_click"
  | "form_submission"
  | "content_engagement"
  | "conversion"
  | "login"
  | "custom_event"
  | "loading_error"
  | "playground_interaction";

interface TrackEventOptions {
  pagePath?: string;
  userId?: string | null;
  userEmail?: string | null;
  extraData?: Record<string, any>;
  location?: string | null;
  googleAdsLabel?: string;
  twitterEventId?: string;
}

interface TrackAnalyticsOptions extends TrackEventOptions {
  nonInteraction?: boolean;
  eventCategory?: string;
  eventAction?: string;
}

// Store tracked events to prevent duplicate tracking
const trackedEvents = new Set<string>();

// Location tracking disabled to avoid requesting geolocation permissions
const fetchLocation = async (): Promise<string | null> => {
  return null;
};

// Generate a unique event key for throttling
const getEventKey = (
  eventType: EventType,
  pagePath: string,
  extraData: any,
) => {
  return `${eventType}:${pagePath}:${JSON.stringify(extraData || {})}`;
};

// Track events to Supabase and other analytics platforms
export const trackEvent = async (
  eventType: EventType,
  options: TrackEventOptions = {},
) => {
  const {
    pagePath = window.location.pathname,
    userId = null,
    userEmail = null,
    extraData = {},
    location = null,
    googleAdsLabel,
    twitterEventId,
  } = options;

  // Throttle events to prevent duplicates within a short time period
  const eventKey = getEventKey(eventType, pagePath, extraData);
  if (trackedEvents.has(eventKey)) {
    console.log(`Tracking throttled for ${eventKey}`);
    return;
  }

  // Add to tracked events and remove after 2 seconds
  trackedEvents.add(eventKey);
  setTimeout(() => trackedEvents.delete(eventKey), 2000);

  let loc = location;
  if (!loc) {
    loc = await fetchLocation();
  }

  // Track to Supabase analytics table
  try {
    await supabase.from("page_events").insert([
      {
        event_type: eventType,
        page_path: pagePath,
        user_id: userId,
        user_email: userEmail,
        extra_data: extraData,
        host_domain: window.location.hostname,
        location: loc,
      },
    ]);
  } catch (error) {
    console.error("Error tracking event to Supabase:", error);
  }

  // Track to Google Analytics/Google Ads if configured
  if (typeof window.gtag === "function") {
    window.gtag("event", eventType, {
      page_path: pagePath,
      ...extraData,
    });
  }

  // Track Google Ads conversion if label is provided
  if (googleAdsLabel) {
    trackGoogleAdsConversion(googleAdsLabel, extraData);
  }

  // Track to Twitter if eventId is provided
  if (twitterEventId && typeof window.twq === "function") {
    window.twq("event", twitterEventId, extraData);
  }

  console.log(`Tracked ${eventType}: ${pagePath}`);
};

// Specific tracking functions for common events
export const trackPageView = (options: TrackEventOptions = {}) => {
  // Get page title from document or options
  const pageTitle = document.title || "";

  // Get referrer information
  const referrer = document.referrer || "";

  // Track with both basic info and any additional data
  return trackEvent("page_view", {
    ...options,
    extraData: {
      title: pageTitle,
      referrer,
      ...options.extraData,
    },
  });
};

export const trackButtonClick = (
  buttonId: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("button_click", {
    ...options,
    extraData: {
      buttonId,
      ...options.extraData,
    },
  });
};

export const trackFormSubmission = (
  formId: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("form_submission", {
    ...options,
    extraData: {
      formId,
      ...options.extraData,
    },
  });
};

export const trackConversion = (
  conversionType: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("conversion", {
    ...options,
    extraData: {
      conversionType,
      ...options.extraData,
    },
  });
};

export const trackLogin = (options: TrackEventOptions = {}) => {
  return trackEvent("login", options);
};

// Generic tracking for custom events like Stripe flows
export const trackCustomEvent = (
  eventName: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("custom_event", {
    ...options,
    extraData: {
      eventName,
      ...options.extraData,
    },
  });
};

export const trackLoadingError = (
  errorType: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("loading_error", {
    ...options,
    extraData: {
      errorType,
      ...options.extraData,
    },
  });
};

export const trackPlaygroundInteraction = (
  action: string,
  options: TrackEventOptions = {},
) => {
  return trackEvent("playground_interaction", {
    ...options,
    extraData: {
      ...options.extraData,
      action,
    },
  });
};
